import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { Message } from '../../models/chat.model';

export interface AtomicTemplateModel {
  value: string;
  label: string;
  icon: string;
}

export interface AtomicTemplateContext {
  currentMessages: Message[];
  pinnedMessages: Message[];
  currentPinnedIndex: number;
  showPinStack: boolean;
  dropdownOpen: boolean;
  isLoading: boolean;
  isTourActive: boolean;
  tourStepId: string | null;
  currentLogo: string;
  logoFading: boolean;
  modelNameFading: boolean;
  modelNameClass: string;
  modelNameLetters: Array<{ char: string; index: number }>;
  currentPlaceholder: string;
  userInput: string;
  audioActive: boolean;
  referencePreviewUrl: string | null;
  imageValidationError: string | null;
  supportsReferenceImage: boolean;
  imageAccept: string;
  showScrollToBottom: boolean;
  generating: boolean;
  selectedModelName: string;
  selectedVideoModel: string;
  uiProgress: number;
  actionToastById: Record<string, { text: string; type: string }>;
  trackMsg: (index: number, message: Message) => string | number;
  trackByLetter: (index: number, item: { char: string; index: number }) => number;
  isLetterWaving: (index: number) => boolean;
  getLetterColor: (index: number) => string;
  fastForwardTransition: () => void;
  scrollToPinned: (next: boolean) => void;
  togglePinStack: (event?: Event) => void;
  clearAllPins: (event?: Event) => void;
  jumpToPinned: (id?: string) => void;
  togglePinMessage: (message: Message) => void;
  getMessageDisplayContent: (message: Message) => string | null;
  convertMarkdown: (content: string) => string;
  getImageUrls: (message: Message) => string[];
  isDalleMessage: (message: Message) => boolean;
  isStabilityMessage: (message: Message) => boolean;
  trackByMedia: (index: number, src: string) => string;
  getVideoUrls: (message: Message) => string[];
  isVideoLike: (message: Message) => boolean;
  getThumbnailUrl: (message: Message) => string | null;
  isVideoPending: (message: Message) => boolean;
  getProgress: (message: Message) => number | null;
  getMediaError: (message: Message) => string | null;
  onActionCopy: (message: Message, index: number) => void;
  toggleActionsMenu: (message: Message, event?: MouseEvent) => void;
  isActionsMenuOpen: (message: Message) => boolean;
  onActionPin: (message: Message) => void;
  onActionCopyToClariChat: (message: Message) => void | Promise<void>;
  firstVideoUrl: (message: Message) => string | null;
  downloadVideo: (videoUrl: string) => void;
  scrollToBottom: () => void;
  onFileSelected: (event: Event) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  autoResize: (textarea: HTMLTextAreaElement) => void;
  onAudioIconClick: () => void;
  sendMessage: () => void | Promise<void>;
  clearImage: () => void;
  toggleDropdown: (event: MouseEvent) => void;
  selectVideoModel: (value: string) => void;
  openModelInfo: () => void;
  // Composer Tools menu
showComposerToolsMenu: boolean;
toggleComposerToolsMenu: (ev?: Event) => void;
repeatPromptFromComposer: () => void;
openComposerAICatalog: () => void;
openComposerAdvisory: () => void;
openComposerModelInfo: () => void;
exportComposerPdf: () => void;
exportComposerDocx: () => void;

}

const DEFAULT_ATOMIC_MODELS: AtomicTemplateModel[] = [
  { value: 'stability', label: 'Stability 3.5', icon: '✨' },
  { value: 'dale', label: 'DALL·E 3', icon: '🎨' },
  { value: 'dale2legacy', label: 'DALL·E 2', icon: '🖼️' },
  { value: 'grokimage', label: 'Grok Image', icon: '🧩' },
  { value: 'googleimage', label: 'Google Image', icon: '🖼️' },
  { value: 'geminiproimage', label: 'Gemini Pro Image', icon: '💎' }
];

@Component({
  selector: 'app-atomic-template',
  templateUrl: './atomic-template.component.html',
  styleUrls: ['./atomic-template.component.scss']
})
export class AtomicTemplateComponent {
  @Input() context!: AtomicTemplateContext;
  @Input() models: AtomicTemplateModel[] = DEFAULT_ATOMIC_MODELS;
  /** When set, model dropdown shows Image section (accordion). */
  @Input() imageModels: AtomicTemplateModel[] = [];
  /** When set, model dropdown shows Video section (accordion). */
  @Input() videoModels: AtomicTemplateModel[] = [];

  imageAccordionOpen = false;
  videoAccordionOpen = true;

  @ViewChild('messagesContainer', { static: true })
  messagesContainer?: ElementRef<HTMLDivElement>;

  @ViewChild('videoTextarea', { static: true })
  videoTextarea?: ElementRef<HTMLTextAreaElement>;

  get useAccordion(): boolean {
    return this.imageModels.length > 0 && this.videoModels.length > 0;
  }

  get displayImageModels(): AtomicTemplateModel[] {
    return this.useAccordion ? this.imageModels : this.models;
  }

  get displayVideoModels(): AtomicTemplateModel[] {
    return this.videoModels;
  }

  get selectedModel(): AtomicTemplateModel | undefined {
    if (!this.context) return this.models[0];
    const id = this.context.selectedVideoModel;
    const fromImage = (this.useAccordion ? this.imageModels : this.models).find(m => m.value === id);
    if (fromImage) return fromImage;
    const fromVideo = this.videoModels.find(m => m.value === id);
    return fromVideo ?? this.models[0];
  }

  toggleImageAccordion(): void {
    this.imageAccordionOpen = !this.imageAccordionOpen;
    if (this.imageAccordionOpen) this.videoAccordionOpen = false;
  }

  toggleVideoAccordion(): void {
    this.videoAccordionOpen = !this.videoAccordionOpen;
    if (this.videoAccordionOpen) this.imageAccordionOpen = false;
  }
}
